import React, { useMemo, useState, useCallback } from 'react';
import { Box, HStack, VStack, Text, Tooltip } from '@chakra-ui/react';
import { useRecoilValue } from 'recoil';
import { nodeMapActiveAtom, nodeCountAtom } from '../state/atoms';
import { getCountryNodeTotals } from '../utils/nodeCountries';

const ORANGE = '#F7931A';

const formatCount = (n) => {
  if (n == null) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const CountryTip = ({ code, name, nodes, share }) => (
  <VStack align="start" spacing={0.5} fontFamily="mono" fontSize="10px" p={1}>
    <HStack spacing={1.5}>
      <Text color={ORANGE} fontWeight="700" letterSpacing="0.1em">{code}</Text>
      <Text color="white" fontWeight="700">{name}</Text>
    </HStack>
    <Text color="whiteAlpha.700">
      Estimated nodes:{' '}
      <Text as="span" color={ORANGE} fontWeight="700">
        {nodes != null ? nodes.toLocaleString() : '—'}
      </Text>
    </Text>
    <Text color="whiteAlpha.700">
      Network share:{' '}
      <Text as="span" color={ORANGE} fontWeight="700">
        {(share * 100).toFixed(1)}%
      </Text>
    </Text>
  </VStack>
);

const CountryRow = ({ code, name, nodes, share, pct }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <Tooltip
      isOpen={isOpen}
      label={<CountryTip code={code} name={name} nodes={nodes} share={share} />}
      placement="right"
      hasArrow
      bg="rgba(5, 8, 12, 0.96)"
      color="white"
      border={`1px solid ${ORANGE}66`}
      boxShadow={`0 0 20px ${ORANGE}22`}
      px={2}
      py={1.5}
    >
      <HStack
        spacing={2}
        fontFamily="mono"
        fontSize="11px"
        align="center"
        cursor="pointer"
        onMouseEnter={open}
        onMouseLeave={close}
        onClick={toggle}
        sx={{ transition: 'background 150ms' }}
        _hover={{ bg: `${ORANGE}0a` }}
        px={1}
        py={0.5}
      >
        <Text w="24px" color={ORANGE} fontWeight="700" letterSpacing="0.08em" flexShrink={0}>
          {code}
        </Text>
        <Text flex="1" color="white" noOfLines={1}>{name}</Text>
        <Box
          w="60px"
          h="4px"
          bg={`${ORANGE}22`}
          border={`1px solid ${ORANGE}55`}
          position="relative"
          flexShrink={0}
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            bottom="0"
            w={`${Math.max(2, pct)}%`}
            bg={`linear-gradient(90deg, ${ORANGE}99, ${ORANGE})`}
            boxShadow={`0 0 5px ${ORANGE}aa`}
          />
        </Box>
        <Text w="58px" textAlign="right" color={ORANGE} fontWeight="700" flexShrink={0}>
          {nodes != null ? `Est. ${formatCount(nodes)}` : '—'}
        </Text>
      </HStack>
    </Tooltip>
  );
};

// Fallback used when the live bitnodes/sidewinder fetch hasn't settled
// yet (or fails silently). Keeps the list showing sensible counts
// instead of "—" placeholders across every row.
const DEFAULT_TOTAL_NODES = 17500;

export const NodeCountriesPanel = () => {
  const nodeMapActive = useRecoilValue(nodeMapActiveAtom);
  const liveTotal = useRecoilValue(nodeCountAtom);
  const totalNodes = liveTotal || DEFAULT_TOTAL_NODES;
  const countries = useMemo(
    () => getCountryNodeTotals(totalNodes),
    [totalNodes]
  );

  if (!nodeMapActive) return null;

  const top = countries.slice(0, 12);
  const topShare = top[0]?.share || 1;

  // Bottom-anchored so the panel sits naturally low on the left edge,
  // just above the welcome/controls cluster. No `top` → panel sizes to
  // Rendered as a child of the Price HUD container in CelestialHud,
  // absolute-positioned at top:100% so it sits directly beneath the
  // NODES count panel regardless of price HUD height.
  return (
    <Box
      position="absolute"
      top="calc(100% + 8px)"
      left={0}
      w={{ base: '260px', md: '300px' }}
      maxH={{ base: '45vh', md: '60vh' }}
      overflowY="auto"
      pointerEvents="auto"
      bg="rgba(5, 8, 12, 0.82)"
      backdropFilter="blur(8px)"
      border={`1px solid ${ORANGE}44`}
      boxShadow={`0 0 20px ${ORANGE}22`}
      p={2.5}
      fontFamily="mono"
      zIndex={2}
      sx={{
        animation: 'tessCtryIn 320ms ease-out',
        '@keyframes tessCtryIn': {
          '0%':   { opacity: 0, transform: 'translateY(-8px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { background: `${ORANGE}55` },
      }}
    >
      <HStack justify="space-between" pb={2} mb={2} borderBottom={`1px solid ${ORANGE}33`}>
        <HStack spacing={1.5}>
          <Box w="5px" h="5px" borderRadius="full" bg={ORANGE} boxShadow={`0 0 6px ${ORANGE}`} />
          <Text fontSize="9px" letterSpacing="0.22em" color={ORANGE} fontWeight="700">
            REACHABLE NODES
          </Text>
        </HStack>
        <Text fontSize="9px" letterSpacing="0.15em" color="whiteAlpha.500">
          {totalNodes ? totalNodes.toLocaleString() : '—'} TOTAL
        </Text>
      </HStack>
      <VStack spacing={0.5} align="stretch">
        {top.map((c) => (
          <CountryRow
            key={c.code}
            code={c.code}
            name={c.name}
            nodes={c.nodes}
            share={c.share}
            pct={(c.share / topShare) * 100}
          />
        ))}
      </VStack>
    </Box>
  );
};
